import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js';
import {
  Alert,
  Box,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Snackbar,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { datadogLogs } from '@datadog/browser-logs';
import CryptoJS from 'crypto-js';
import { IProduct } from '../../core/interfaces/product.interface';
import { ProjectDialogFooter } from './ProjectDialogFooter';
import { ProjectDialogForm } from './ProjectDialogForm';
import { ConfirmationDialog } from '../ConfirmationDialog';
import { IProject } from '../../core/interfaces/project.interface';
import { IAOIInfo } from '../../core/interfaces/aoi-info.interface';
import useGentian from '../../core/hooks/useGentian';
import { useQueryClient } from 'react-query';
import { useProgress, useUser } from '../../core/hooks';
import { Res } from '../../resources';
import { LoadingBackdrop } from '../LoadingBackdrop';
import { CheckoutPopUp } from './CheckoutPopUp';
import { isReseller } from '../../Utils/account.utils';
import { ICredit } from '../../core/interfaces/credit.interface';
import { calculatePriceByType, PricingType } from '../../Utils/pricing.utils';

const stripePromise = loadStripe(Res.api.stripe.PUBLISHABLE_KEY);

export interface ProjectDialogProps {
  AOIInfo?: IAOIInfo;
  onClose?: () => void;
  onSuccess?(project: IProject): void;
  product?: IProduct;
  handleCloseAll: () => void;
  isTrueSightOver100ha?: boolean;
  isTrueSightOver40ha?: boolean;
  showCheckoutPopup?: boolean;
  setShowCheckoutPopup?: (open: boolean) => void;
  checkoutModalOpen?: boolean;
  setCheckoutModalOpen?: (open: boolean) => void;
}

const dataLayer = window['dataLayer'] || [];

export const ProjectDialog: FC<ProjectDialogProps> = ({
  AOIInfo,
  onClose,
  onSuccess,
  product,
  handleCloseAll,
  isTrueSightOver100ha,
  isTrueSightOver40ha,
  showCheckoutPopup,
  setShowCheckoutPopup,
  checkoutModalOpen,
  setCheckoutModalOpen,
}) => {
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const {
    data: progressData,
    setSseUrl,
    setProjectName: setProgressProjectName,
    setIsProgressPopupOpen,
  } = useProgress();
  const { clientId } = useUser();
  const { notifyCustomer, notifyResellerLimit } = useGentian();

  // States
  const [projectName, setProjectName] = useState('');
  const [projectDescription, setProjectDescription] = useState('');
  const [resellerCustomerName, setResellerCustomerName] = useState('');
  const [resellerExternalProjectId, setResellerExternalProjectId] = useState('');
  const [resellerNotes, setResellerNotes] = useState('');
  const [projectInfo, setProjectInfo] = useState<IProject>();
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [paymentSessionId, setPaymentSessionId] = useState('');
  const [instantHabMapCreated, setInstantHabMapCreated] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false);

  // 🔹 prevent duplicate project creation
  const paymentHandledRef = useRef(false);

  const { mutate: createInstantHabMap, isLoading: isCreatingInstantHabMap } =
    useGentian().createInstantHabmap;
  const { mutate: createProject } = useGentian().createProject;
  const { data: accountData } = useGentian().useAccount();
  const {
    mutate: createCheckoutSession,
    error: checkoutError,
    data: checkoutData,
  } = useGentian().createCheckoutSession;
  const account = accountData?.data;
  const isResellerAccount = isReseller(accountData);
  const isTrueSageProduct =
    product?._id === Res.ids.products.TRUE_SAGE || product?.name === Res.product_names.TRUE_SAGE;
  const { data: paymentStatusData } = useGentian().usePaymentStatus(paymentSessionId);

  // --- Stripe session handling ---
  useEffect(() => {
    if (checkoutData?.sessionId) setPaymentSessionId(checkoutData.sessionId);
  }, [checkoutData?.sessionId]);

  useEffect(() => {
    if (checkoutError) {
      setSnackbarMessage(`Error on processing the checkout. ${checkoutError}`);
      datadogLogs.logger.error(`Error on processing the checkout. ${checkoutError}`, {
        error: checkoutError,
        account: accountData?.data,
        product: { id: product?._id, name: product?.name },
        project: { name: projectInfo?.name, totalArea: projectInfo?.totalArea },
      });
      setProjectInfo(undefined);
    }
  }, [checkoutError, accountData, product, projectInfo]);

  // --- Project creation logic ---
  const pricingType = useMemo<PricingType>(() => {
    if (isTrueSageProduct) return isResellerAccount ? 'truesageResellers' : 'truesage';
    return isResellerAccount ? 'resellers' : 'default';
  }, [isResellerAccount, isTrueSageProduct]);

  const projectPrice = useMemo(() => {
    const baseCost = calculatePriceByType(AOIInfo?.TotalArea || 0, pricingType);
    const country = account?.user_metadata?.country || account?.country || '';
    const isUK = typeof country === 'string' && country.trim().toLowerCase() === 'u.k.';
    return isUK ? Math.round(baseCost * 1.2 * 100) / 100 : baseCost;
  }, [AOIInfo?.TotalArea, account?.country, account?.user_metadata?.country, pricingType]);

  const getProjectInfo = useCallback(() => {
    const project: IProject = {
      name: projectName,
      description: isResellerAccount ? resellerNotes : projectDescription,
      AOI: AOIInfo?.shape,
      products: [{ name: product?.name, id: product?._id }],
      totalArea: AOIInfo?.TotalArea,
      unit: AOIInfo?.units,
      AOIThumbnail: AOIInfo?.thumbnail,
      customerName: account?.name || account?.user_metadata?.name || account?.email || 'Anonymous',
      email: account?.email || projectInfo?.email || 'Anonymous',
      company: account?.user_metadata?.company || 'Anonymous',
      jobTitle: account?.user_metadata?.jobTitle || 'Anonymous',
      country: account?.user_metadata?.country || 'Anonymous',
      projectHash: undefined,
      price: projectPrice,
      resellerCustomerName: resellerCustomerName || undefined,
      resellerExternalProjectId: resellerExternalProjectId || undefined,
      resellerNotes: resellerNotes || undefined,
    };

    project.projectHash = CryptoJS.SHA256(JSON.stringify(project)).toString(CryptoJS.enc.Hex);
    return project;
  }, [
    AOIInfo,
    account,
    product,
    projectName,
    projectDescription,
    projectPrice,
    resellerCustomerName,
    resellerExternalProjectId,
    resellerNotes,
  ]);
  const { useCreditByUserId, createOrUpdateCredit, useAccount } = useGentian();

  const { data: creditData, isLoading, isError, refetch } = useCreditByUserId(account?.user_id as string);
  const currentUsed = creditData?.amountUsed;
  const limit = creditData?.debtLimit;
  const currentProjectPrice = calculatePriceByType(AOIInfo?.TotalArea || 0, pricingType);

  const isOverCreditLimit = isResellerAccount && currentUsed > limit;
  const willExceedCreditLimit = isResellerAccount && currentUsed + currentProjectPrice > limit;
  const creditBlocked = isOverCreditLimit || willExceedCreditLimit;

  const handleSubmitProject = () => {
    const project = getProjectInfo();

    createProject(project, {
      onSuccess: (createdProject: any) => {
        queryClient.invalidateQueries('projects');
        onSuccess?.(project);
        const createdProjectId =
          createdProject?._id || createdProject?.projectId || createdProject?.id || project?._id;

        // Update reseller credit if applicable using createOrUpdateCredit
        if (isResellerAccount) {
          const payload: ICredit & { customerName: string; email: string } = {
            _id: creditData?._id, // existing credit id if any
            userId: accountData?.data?.user_id as string,
            customerName: accountData?.data?.name || 'Anonymous',
            email: accountData?.data?.email || 'Anonymous',
            debtLimit: creditData?.debtLimit ?? 0,
            amountUsed: (creditData?.amountUsed ?? 0) + project.price, // increment amountUsed
            totalSpend: (creditData?.totalSpend ?? 0) + project.price,
            createdAt: creditData?.createdAt ?? new Date(),
            updatedAt: new Date(),
            lastPaidAt: creditData?.lastPaidAt ?? new Date(),
            updatedBy: accountData.data.user_id as string,
          };

          createOrUpdateCredit.mutate(payload, {
            onSuccess: (updatedCredit) => {
              console.log('Reseller amountUsed updated:', updatedCredit);
              refetch(); // refetch credit data

              const resellerEmail = accountData?.data?.email || project.email;
              const debtLimitValue = (updatedCredit as ICredit | undefined)?.debtLimit ?? payload.debtLimit;
              const newUsedValue = (updatedCredit as ICredit | undefined)?.amountUsed ?? payload.amountUsed;
              const prevUsedValue = creditData?.amountUsed ?? 0;
              const prevLimitValue = creditData?.debtLimit ?? payload.debtLimit;

              const prevPercent = prevLimitValue > 0 ? (prevUsedValue / prevLimitValue) * 100 : 0;
              const newPercent = debtLimitValue > 0 ? (newUsedValue / debtLimitValue) * 100 : 0;

              if (
                createdProjectId &&
                resellerEmail &&
                prevPercent < 80 &&
                newPercent >= 80 &&
                Number.isFinite(newPercent)
              ) {
                notifyResellerLimit.mutate({
                  projectId: String(createdProjectId),
                  resellerEmail,
                  usagePercent: Math.round(newPercent),
                });
              }
            },
            onError: (err) => {
              console.error('Failed to update reseller amountUsed', err);
            },
          });
        }
      },
      onError: (error) => {
        setSnackbarMessage(`Failed to create project. Please try again later. ${error}`);
        setSnackbarOpen(true);
      },
    });
  };

  const notifySentRef = useRef(false);
  const submitInstantHabMapProject = useCallback(
    (projectInfo: IProject) => {
      if (instantHabMapCreated) return;
      setInstantHabMapCreated(true);

      createInstantHabMap(projectInfo, {
        onSuccess: (data) => {
          const projectId = data.projectId;
          const sseURL = `${Res.api.gentian.BFF_BASE_URL}/instant-hab-map/${projectId}/${clientId}/progress`;

          setSseUrl(sseURL);
          setProgressProjectName(projectName);

          // Only open the popup for new projects
          if (isTrueSightOver40ha && !isTrueSightOver100ha) {
            setShowCheckoutPopup(true);
            setIsProgressPopupOpen(false);
            setCheckoutModalOpen(false);
          } else {
            // handleCloseAll();
            setIsProgressPopupOpen(true);
            setCheckoutModalOpen(false);
            setShowConfirmation(false);
          }

          const eventSource = new EventSource(sseURL);

          eventSource.onmessage = (event) => {
            if (notifySentRef.current) return;
            try {
              const progressUpdate = JSON.parse(event.data);
              console.log('SSE message received:', progressUpdate);
              console.log('ProjectDialog → notifySentRef.current:', notifySentRef.current);

              if (
                progressUpdate?.progress >= 0.99 ||
                (progressData?.progress >= 0.99 && !notifySentRef.current)
              ) {
                notifySentRef.current = true;
                console.log('ProjectDialog → notifySentRef AFTER SET:', notifySentRef.current);
                notifyCustomer.mutate(projectId, {
                  onSuccess: () => console.log(`Notification email sent for project ${projectId}`),
                  onError: (err) => console.error('Failed to send customer notification', err),
                });
                eventSource.close();
              }
            } catch (err) {
              console.error('Error parsing SSE event:', err);
            }
          };

          eventSource.onerror = (err) => {
            console.error('SSE connection error:', err);
            eventSource.close();
            if (!notifySentRef.current) {
              setTimeout(() => {
                console.log('Reconnecting SSE...');
                const newEventSource = new EventSource(sseURL);
                newEventSource.onmessage = eventSource.onmessage;
                newEventSource.onerror = eventSource.onerror;
              }, 5000);
            }
          };
        },
        onError: (err) => {
          setSnackbarMessage('Error on submitting Instant HabMap Project');
          datadogLogs.logger.error('Error on submitting Instant HabMap Project', {
            error: err,
            project: projectInfo,
          });
        },
      });
    },
    [
      createInstantHabMap,
      clientId,
      projectName,
      setSseUrl,
      setProgressProjectName,
      setIsProgressPopupOpen,
      handleCloseAll,
      isTrueSightOver40ha,
      isTrueSightOver100ha,
      notifyCustomer,
      instantHabMapCreated,
      setShowCheckoutPopup,
    ],
  );

  // 🔹 Run on payment success
  useEffect(() => {
    if (!projectInfo || !paymentStatusData?.status) return;
    if (paymentHandledRef.current) return;

    if (paymentStatusData.status === 'complete' && paymentStatusData.paymentStatus === 'paid') {
      paymentHandledRef.current = true;

      dataLayer.push({
        event: 'purchase',
        quoteCategory: 'True Sight', //
        purchase_value: calculatePriceByType(projectInfo.totalArea, pricingType),
        transactionId: projectInfo._id, // optional: unique ID for purchase tracking
        quoteFormURL: location.pathname.toLowerCase(),
      });

      // ✅ Handle tier logic
      if (isTrueSightOver100ha) {
        handleSubmitProject();
        setInstantHabMapCreated(false); // reset for next project
      } else {
        submitInstantHabMapProject(projectInfo);
      }
    }
  }, [
    paymentStatusData,
    projectInfo,
    submitInstantHabMapProject,
    isTrueSightOver100ha,
    instantHabMapCreated,
    pricingType,
  ]);

  const handleOnBuyNow = async () => {
    const newProjectInfo = getProjectInfo();
    setProjectInfo(newProjectInfo);
    setInstantHabMapCreated(false); // 🔹 reset for every new project
    paymentHandledRef.current = false; // 🔹 reset guard
    await queryClient.invalidateQueries(['checkout-session']);
    createCheckoutSession(newProjectInfo);
  };

  const handleCloseSnackbar = () => setSnackbarOpen(false);

  const getTooltipText = () =>
    !projectName ? 'Please enter a project name to continue' : `Request a survey for ${product?.name}`;

  const isProjectNameValid = projectName.length > 0 && !/[^a-zA-Z0-9 ,._-]/.test(projectName);

  return (
    <>
      <DialogTitle component="div">
        <Typography variant="h5" color="var(--gentian-dark-gray)">
          {product?.name}
        </Typography>
      </DialogTitle>
      <IconButton aria-label="close" onClick={onClose} sx={{ position: 'absolute', top: 12, right: 12 }}>
        <Close />
      </IconButton>
      <Divider sx={{ marginX: '16px' }} />
      <DialogContent
        sx={{
          minWidth: isMobile ? '100vw' : '480px',
          backgroundColor: 'white',
          padding: 'var(--content-spacing-narrow)',
        }}
      >
        {!!checkoutData?.clientSecret && paymentStatusData?.status !== 'complete' ? (
          <Box sx={{ boxSizing: 'border-box', minHeight: '680px', margin: '-16px', paddingBottom: '16px' }}>
            <EmbeddedCheckoutProvider
              stripe={stripePromise}
              options={{ clientSecret: checkoutData?.clientSecret }}
            >
              <EmbeddedCheckout />
            </EmbeddedCheckoutProvider>
          </Box>
        ) : (
          <>
            <ProjectDialogForm
              isInstantHabMap={product?._id === Res.ids.products.TRUE_SIGHT}
              isReseller={isResellerAccount}
              projectPrice={projectPrice}
              AOIInfo={AOIInfo}
              projectName={projectName}
              projectDescription={projectDescription}
              resellerCustomerName={resellerCustomerName}
              resellerExternalProjectId={resellerExternalProjectId}
              resellerNotes={resellerNotes}
              setProjectName={setProjectName}
              setProjectDescription={setProjectDescription}
              setResellerCustomerName={setResellerCustomerName}
              setResellerExternalProjectId={setResellerExternalProjectId}
              setResellerNotes={setResellerNotes}
              creditBlocked={creditBlocked}
              creditWarningMessage={
                isOverCreditLimit
                  ? 'You have exceeded your debt limit. Please make a payment to continue.'
                  : willExceedCreditLimit
                    ? 'This project will exceed your debt limit. Please make a payment to continue.'
                    : undefined
              }
            />
            <Divider sx={{ marginY: '16px' }} />
            <ProjectDialogFooter
              disabled={!projectName || creditBlocked}
              text={getTooltipText()}
              onClose={onClose}
              onContinue={handleSubmitProject}
              onBuyNow={handleOnBuyNow}
              isInstantHabMap={product?._id === Res.ids.products.TRUE_SIGHT}
              isProjectNameValid={isProjectNameValid}
            />
          </>
        )}
      </DialogContent>
      <ConfirmationDialog
        title="Confirmation"
        open={false}
        onClose={() => setShowConfirmation(false)}
        onAgree={() => handleSubmitProject()}
      >
        <Typography>
          You are about to submit a request for a quote for {product?.name}. Please confirm your request.
        </Typography>
      </ConfirmationDialog>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="error" sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
      <LoadingBackdrop open={isCreatingInstantHabMap}>Creating project...</LoadingBackdrop>
    </>
  );
};
